/**
 * Modifier weight price ranges - the shape they arrive in, the shape a form edits them in, and the
 * conversion between.
 *
 * Two surfaces build these: the operation drawer, creating ranges from nothing, and the store
 * operations dialog, reading a store's existing ranges back to change one. The second is why this
 * module exists - a round trip must not quietly alter the parts the user did not touch.
 */

import { v4 as uuid } from 'uuid';
import { formatCents, parseCentsInput } from './namedPriceUtils';

/**
 * One range as a form row. Bounds and price are strings because they are mid-edit; `WeightPriceRanges`
 * renders exactly this.
 */
export interface RangeItem {
  id: string;
  start: string;
  end: string;
  price: string;
  applyModifierWeightUpcharge: boolean;
}

/** One range as the API states it, on both the read and the write side. */
export interface WeightPriceRange {
  rangeStart: number | null;
  /** Null on the last range, meaning open-ended - "3 or more". */
  rangeEnd: number | null;
  authoringPrice?: { amount: number };
  applyModifierWeightUpcharge?: boolean;
}

/**
 * Parses a range bound. Bounds are weights, not money: the API types them `NonNegativeFloat`, so a
 * fractional bound is legal and must survive a round trip rather than being truncated to an integer.
 *
 * Returns null for an empty or unparseable bound, which on a final range means open-ended.
 */
const parseBound = (value: string): number | null => {
  const trimmed = (value ?? '').trim();
  if (trimmed === '') return null;

  const bound = Number(trimmed);
  return Number.isFinite(bound) && bound >= 0 ? bound : null;
};

/** True when this row's end was deliberately left empty, i.e. the range is open-ended. */
const isOpenEnded = (row: RangeItem): boolean => (row.end ?? '').trim() === '';

/**
 * Turns the API's ranges into editable form rows.
 *
 * `rangeEnd: null` becomes an empty string - real payloads carry it on the last range, and reading it
 * as a value rather than as "open-ended" is what made this throw before.
 */
export const rangesFromWeightPriceRanges = (ranges?: WeightPriceRange[] | any[]): RangeItem[] =>
  (ranges || []).map((range: any) => ({
    id: uuid(),
    start: range?.rangeStart == null ? '' : String(range.rangeStart),
    end: range?.rangeEnd == null ? '' : String(range.rangeEnd),
    price: range?.authoringPrice?.amount == null ? '' : String(range.authoringPrice.amount),
    // Only reached for payloads predating the field being selected; the endpoint returns it now.
    applyModifierWeightUpcharge: range?.applyModifierWeightUpcharge ?? true,
  }));

/**
 * Turns form rows back into the API's ranges.
 *
 * The price is whole cents: `authoringPrice.amount` is a `NonNegativeInt`, so a fractional entry is not
 * a rounding question, it is an invalid request. Such a price is omitted rather than coerced, and
 * `validateRanges` is what stops it reaching here.
 */
export const weightPriceRangesFromRanges = (rows: RangeItem[]): WeightPriceRange[] =>
  (rows || []).map((row) => {
    const amount = parseCentsInput(row.price);

    return {
      rangeStart: parseBound(row.start),
      rangeEnd: parseBound(row.end),
      ...(amount !== null && { authoringPrice: { amount } }),
      applyModifierWeightUpcharge: row.applyModifierWeightUpcharge ?? true,
    };
  });

export interface ValidateRangesOptions {
  /**
   * Whether the final range may be left open-ended.
   *
   * True where existing ranges are being edited: real patches contain open-ended final ranges, and
   * demanding an end value there would silently close one. The drawer leaves this off, keeping its
   * current behaviour of requiring every bound - the API permits open-ended ranges, so that strictness
   * is the drawer's own, not the contract's.
   */
  allowOpenEndedLast?: boolean;
}

/** Whether these rows can be submitted: ascending bounds, and a whole-cent price on each. */
export const validateRanges = (
  rows: RangeItem[],
  { allowOpenEndedLast = false }: ValidateRangesOptions = {}
): boolean => {
  if (!Array.isArray(rows) || rows.length === 0) return false;

  return rows.every((row, index) => {
    const start = parseBound(row.start);
    if (start === null) return false;
    if (parseCentsInput(row.price) === null) return false;

    const end = parseBound(row.end);
    if (end === null) {
      return allowOpenEndedLast && index === rows.length - 1 && isOpenEnded(row);
    }

    return end > start;
  });
};

/**
 * A one-line summary of a set of ranges, for a table cell that has no room for the editor.
 *
 * Open-ended reads as "3+" rather than "3 to null". An unpriced range shows its bounds without a price
 * instead of inventing one.
 */
export const summarizeWeightPriceRanges = (ranges?: WeightPriceRange[] | any[]): string =>
  (ranges || [])
    .map((range: any) => {
      const start = range?.rangeStart ?? 0;
      const bounds = range?.rangeEnd == null ? `${start}+` : `${start}–${range.rangeEnd}`;
      const amount = range?.authoringPrice?.amount;

      return amount == null ? bounds : `${bounds} ${formatCents(amount)}`;
    })
    .join(', ');
